import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateField } from '@/components/dateFields';
import { appDateTime } from '@/utils/appDateFormat';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronRight, Download, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import Input from '../../../../components/ui/Input';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Spinner } from '../../../../components/ui/Spinner';
import Pagination from '../../../../components/Pagination';
import {
  exportSecurityAuditPage,
  getSecurityAuditEvents,
  getSecurityAuditEventTypes,
  SecurityAuditEvent,
  SecurityAuditEventType,
  SecurityAuditQuery,
} from '../../../../api/General/SecurityAudit';
import { todayLocalDateParam } from '@/utils/apiDate';

/**
 * CO-322 — the append-only security audit log.
 *
 * Scope is decided by the SERVER, never here: HR and Admin get their own company's events, an Operator
 * gets platform events only (the Role Model keeps Operator out of company data). The page only explains
 * which scope the viewer is looking at. Viewing and exporting are themselves audited.
 *
 * Design (impeccable, product register): restrained, dense but legible. Filters sit inline above the table
 * and wrap on narrow screens; row details open inline under the row (no modal). Logical properties only.
 */

const OUTCOME_CODES: Record<string, number> = { Success: 0, Denied: 1, Failed: 2 };
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

interface Filters {
  from: string;
  to: string;
  eventType: string;
  outcome: string;
  actor: string;
  targetUser: string;
}

const EMPTY_FILTERS: Filters = { from: '', to: '', eventType: '', outcome: '', actor: '', targetUser: '' };

function roleList(roles: unknown): string[] {
  if (Array.isArray(roles)) return roles.map(String);
  return typeof roles === 'string' ? roles.split(',').map((r) => r.trim()) : [];
}

const SecurityAudit: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Same store path MasterLayout reads. Copy only; the server decides the scope.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const role = useSelector((state: any) => state?.user?.role);
  const roleNames = roleList(role);
  const isOperator = roleNames.includes('Operator');
  // Copy only: the server already filters employee-data rows and event types for an Admin without HR.
  const isAdminWithoutHr = roleNames.includes('Admin') && !roleNames.includes('Hr');

  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState<SecurityAuditEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [eventTypes, setEventTypes] = useState<SecurityAuditEventType[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const query: SecurityAuditQuery = useMemo(() => ({
    from: applied.from || undefined,
    to: applied.to || undefined,
    eventType: applied.eventType !== '' ? Number(applied.eventType) : undefined,
    outcome: applied.outcome !== '' ? OUTCOME_CODES[applied.outcome] : undefined,
    actor: applied.actor.trim() || undefined,
    targetUser: applied.targetUser.trim() || undefined,
    pageNumber,
    pageSize,
  }), [applied, pageNumber, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const result = await getSecurityAuditEvents(query);
      if (result.errors?.length || !result.payload) {
        setLoadError(true);
        setRows([]);
        return;
      }
      setRows(result.payload.items ?? []);
      setTotalCount(result.payload.totalCount ?? 0);
      setTotalPages(result.payload.totalPages ?? 0);
    } catch {
      setLoadError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    getSecurityAuditEventTypes()
      .then((r) => { if (!r.errors?.length && r.payload) setEventTypes(r.payload); })
      .catch(() => undefined);
  }, []);

  const eventLabel = (name: string) => t(`securityAudit.eventType.${name}`, { defaultValue: name });
  const outcomeLabel = (name: string) => t(`securityAudit.outcome.${name}`, { defaultValue: name });

  const formatWhen = (utc: string) => {
    const d = new Date(utc);
    if (Number.isNaN(d.getTime())) return utc;
    return appDateTime(d, true);
  };

  const outcomeBadge = (outcome: string) => {
    const variant = outcome === 'Success' ? 'success' : outcome === 'Denied' ? 'warning' : 'error';
    return <StatusBadge variant={variant}>{outcomeLabel(outcome)}</StatusBadge>;
  };

  const applyFilters = () => {
    setPageNumber(1);
    setExpandedId(null);
    setApplied({ ...draft });
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPageNumber(1);
    setExpandedId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportSecurityAuditPage(query);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `security-audit-${todayLocalDateParam()}-p${pageNumber}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('securityAudit.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const eventTypeOptions = [
    { value: '', label: t('securityAudit.allEventTypes') },
    ...eventTypes.map((e) => ({ value: String(e.code), label: eventLabel(e.name) })),
  ];
  const outcomeOptions = [
    { value: '', label: t('securityAudit.allOutcomes') },
    ...Object.keys(OUTCOME_CODES).map((o) => ({ value: o, label: outcomeLabel(o) })),
  ];

  const personCell = (name?: string | null, id?: string | null) => {
    if (!id) return <span className="text-muted-foreground">{t('securityAudit.system')}</span>;
    return (
      <span className="block max-w-[14rem] truncate" title={id}>
        {name || <span className="font-mono text-xs">{id}</span>}
      </span>
    );
  };

  return (
    <PageLayout
      title={t('Security Audit Log')}
      description={isOperator ? t('securityAudit.scopePlatform') : t('securityAudit.scopeCompany')}
      icon={ShieldCheck}
      accentColor="slate"
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || loading || rows.length === 0}
          data-testid="security-audit-export"
        >
          <Download className="h-4 w-4 me-2" aria-hidden="true" />
          {t('securityAudit.exportPage')}
        </Button>
      }
    >
      <div className="mx-4 sm:mx-6 my-4 space-y-4" data-testid="security-audit-page">
        {!isOperator && isAdminWithoutHr && (
          <p className="text-sm text-muted-foreground" data-testid="security-audit-admin-scope-note">
            {t('securityAudit.adminScopeNote')}
          </p>
        )}
        <form
          className="rounded-xl border border-border bg-card p-4"
          onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
          aria-label={t('securityAudit.filters')}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <DateField
              label={t('securityAudit.from')}
              value={draft.from}
              max={draft.to || undefined}
              onChange={(v) => setDraft({ ...draft, from: v })}
              data-testid="security-audit-from"
            />
            <DateField
              label={t('securityAudit.to')}
              value={draft.to}
              min={draft.from || undefined}
              onChange={(v) => setDraft({ ...draft, to: v })}
              data-testid="security-audit-to"
            />
            <Select
              label={t('securityAudit.eventTypeLabel')}
              options={eventTypeOptions}
              value={draft.eventType}
              searchable
              onChange={(v: string | number) => setDraft({ ...draft, eventType: String(v ?? '') })}
              data-testid="security-audit-event-type"
            />
            <Select
              label={t('securityAudit.outcomeLabel')}
              options={outcomeOptions}
              value={draft.outcome}
              onChange={(v: string | number) => setDraft({ ...draft, outcome: String(v ?? '') })}
              data-testid="security-audit-outcome"
            />
            <Input
              label={t('securityAudit.actor')}
              placeholder={t('securityAudit.userPlaceholder')}
              value={draft.actor}
              onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
              data-testid="security-audit-actor"
            />
            <Input
              label={t('securityAudit.targetUser')}
              placeholder={t('securityAudit.userPlaceholder')}
              value={draft.targetUser}
              onChange={(e) => setDraft({ ...draft, targetUser: e.target.value })}
              data-testid="security-audit-target"
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
              {t('securityAudit.reset')}
            </Button>
            <Button type="submit" size="sm" data-testid="security-audit-apply">
              {t('securityAudit.apply')}
            </Button>
          </div>
        </form>

        <div className="rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {t('securityAudit.count', { count: totalCount })}
            </p>
            <p className="text-xs text-muted-foreground">{t('securityAudit.appendOnly')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : loadError ? (
            <div className="px-4 py-12 text-center" role="alert">
              <p className="text-sm text-foreground">{t('securityAudit.loadFailed')}</p>
              <Button className="mt-3" variant="outline" size="sm" onClick={() => void load()}>
                {t('Retry')}
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="security-audit-empty">
              {t('securityAudit.empty')}
            </p>
          ) : (
            <Table data-testid="security-audit-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"><span className="sr-only">{t('securityAudit.details')}</span></TableHead>
                  <TableHead>{t('securityAudit.when')}</TableHead>
                  <TableHead>{t('securityAudit.event')}</TableHead>
                  <TableHead>{t('securityAudit.outcomeLabel')}</TableHead>
                  <TableHead>{t('securityAudit.actor')}</TableHead>
                  <TableHead>{t('securityAudit.target')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('securityAudit.source')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const open = expandedId === row.id;
                  const target = row.targetName || row.targetUserId
                    ? personCell(row.targetName, row.targetUserId)
                    : (
                      <span className="block max-w-[14rem] truncate">
                        {[row.targetType, row.targetId].filter(Boolean).join(' · ') || '·'}
                      </span>
                    );
                  return (
                    <Fragment key={row.id}>
                      <TableRow data-testid="security-audit-row" data-event-type={row.eventType}>
                        <TableCell>
                          <button
                            type="button"
                            className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                            aria-expanded={open}
                            aria-label={t('securityAudit.details')}
                            onClick={() => setExpandedId(open ? null : row.id)}
                          >
                            {open
                              ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                              : <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />}
                          </button>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">{formatWhen(row.occurredUtc)}</TableCell>
                        <TableCell>{eventLabel(row.eventType)}</TableCell>
                        <TableCell>{outcomeBadge(row.outcome)}</TableCell>
                        <TableCell>{personCell(row.actorName, row.actorUserId)}</TableCell>
                        <TableCell>{target}</TableCell>
                        <TableCell className="hidden md:table-cell font-mono text-xs" dir="ltr">{row.ip || '·'}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={7}>
                            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 py-2 text-xs sm:grid-cols-2">
                              {([
                                ['securityAudit.roles', row.actorRoles],
                                ['securityAudit.targetRecord', [row.targetType, row.targetId].filter(Boolean).join(' · ')],
                                ['securityAudit.request', [row.httpMethod, row.path].filter(Boolean).join(' ')],
                                ['securityAudit.source', row.ip],
                                ['securityAudit.userAgent', row.userAgent],
                                ['securityAudit.correlationId', row.correlationId],
                                ['securityAudit.detailsJson', row.detailsJson],
                                ['securityAudit.hash', row.hash],
                              ] as [string, string | null | undefined][]).filter(([, v]) => v).map(([key, value]) => (
                                <div key={key} className="min-w-0">
                                  <dt className="text-muted-foreground">{t(key)}</dt>
                                  <dd className="break-all font-mono text-foreground" dir="ltr">{value}</dd>
                                </div>
                              ))}
                            </dl>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="border-t border-border px-4 py-3">
              <Pagination
                currentPage={pageNumber}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
                onPageChange={(p) => { setPageNumber(p); setExpandedId(null); }}
              />
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default SecurityAudit;
